/**
 * AdminHeader Organism
 *
 * The header component for admin pages.
 * Contains logo, location selector, language selector, and logout.
 */

'use client';

import { useTranslation } from '@/app/i18n/client';
import FullScreenLoading from '@/components/fullScreenLoading';
import MaspLogo from '@/components/icons/maspLogo';
import { LanguageSelector } from '@/components/molecules/navigation';
import { HeaderNotificationButton } from '@/components/notifications';
import useLocation from '@/context/LocationProvider';
import { useAuthV2 } from '@/hooks/useAuthV2';
import useCart from '@/hooks/useCart';
import useDonation from '@/hooks/useDonation';
import { getNameLocations } from '@/utils/locations';
import { UserGroups } from '@/utils/types';
import { LogOutIcon, MapPin } from 'lucide-react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

const AdminHeader = () => {
  const { lng } = useParams() as { lng: string };
  const { t } = useTranslation(lng, 'admin');

  const { signOut } = useAuthV2();
  const { userData, userGroups } = useAuthV2();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const { setCart } = useCart();
  const { setDonation } = useDonation();
  const isAdmin = userGroups.includes(UserGroups.ADMIN) || userGroups.includes(UserGroups.STAFF);
  const { locationModal, setIsOpenModalSetLocation } = useLocation();

  const signOutUser = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      setCart([]);
      setDonation(0);
    } catch (error) {
      setIsSigningOut(false);
    }
  };

  return (
    <>
      <header
        className={twMerge(
          'fixed inset-x-0 top-0 z-50 flex h-16 w-full items-center justify-between border-b border-gray2 bg-background px-6 text-foreground'
        )}>
        <Link href='/pt-BR/admin/experiences'>
          <MaspLogo size={40} />
        </Link>
        <div className='flex items-center gap-6'>
          <HeaderNotificationButton />

          {userData?.name && <span>Olá, {userData.name}</span>}

          {isAdmin && (
            <div className='flex cursor-pointer items-center gap-2' onClick={() => setIsOpenModalSetLocation(true)}>
              <MapPin className='h-5 w-5 text-primary-700' size={16} />
              {locationModal ? (
                <p className='text-primary-700'>{getNameLocations(locationModal)}</p>
              ) : (
                <p className='text-primary-700'>Selecione o local</p>
              )}
            </div>
          )}

          <div className='h-10 w-px bg-zinc-500' />

          <LanguageSelector />
          <>
            <div className='h-10 w-px bg-zinc-500' />
            <div onClick={signOutUser} className='flex cursor-pointer items-center'>
              <span className='mr-2 text-sm'>{t('logout')}</span>
              <LogOutIcon size={16} />
            </div>
          </>
        </div>
      </header>
      <FullScreenLoading text={t('signingOut')} showLoading={isSigningOut} />
    </>
  );
};

export { AdminHeader };
export default AdminHeader;
